import { t } from "@repo/shared/i18n";
import type {
  EventToken,
  ObjectiveDisplay,
  PriceInfo,
} from "@repo/shared/types";
import {
  type PriceIcon,
  getObjectiveDisplayInfo as sharedObjectiveDisplayInfo,
  getPriceDisplay as sharedPriceDisplay,
} from "@repo/shared/utils";

import { convertPlayerPrice } from "./convertPlayerPrice";

export { PRICE_SOURCE_LABEL_KEYS } from "@repo/shared/utils";

/*
  What a price cell shows is decided in `@repo/shared/utils` (priceDisplay), so
  the app shows the same thing. This layer only draws it the web's way: its own
  icon files, defaulting to the Vite env's assets base URL, and its own words.
*/
const PRICE_ICON_FILES = {
  coin: "coin-tmp.png",
  sbc: "sbc-tmp.png",
  objective: "obj-tmp.png",
  sp: "sp-tmp.png",
} as const satisfies Record<Exclude<PriceIcon, "token">, string>;

/** A shared `PriceIcon` as the web's image. */
export const priceIconUrl = (
  icon: PriceIcon | null,
  tokenIconKey: string | null,
  base: string,
): string | null => {
  if (icon === null) return null;
  if (icon === "token") return `${base}/${tokenIconKey}`;
  return `${base}/misc/${PRICE_ICON_FILES[icon]}`;
};

const tokensFallbackName = () =>
  t("players:detailed.currentState.priceSource.tokens");

export interface PriceDisplay {
  /** `null` for a token missing from the event token catalog: no icon is shown. */
  iconUrl: string | null;
  /** Text shown before or instead of the number, e.g. "-". `null` when only a number (or nothing) is shown. */
  label: string | null;
  /** Whether the caller should render `displayPrice` next to the icon. */
  showNumber: boolean;
  /** The value to render when `showNumber` is true (a coin price or token count). */
  displayPrice: number;
  /** The value to render when `showNumber` is true (a coin price or token count). */
  launchPrice: number;
  /** `tokens` only: the catalog token name, or the generic "Tokens" when the token is not in the catalog. */
  tokenName: string | null;
}

export const getPriceDisplay = (
  priceInfo: PriceInfo | undefined,
  eventTokens: EventToken[] = [],
  assetsBaseUrl: string = import.meta.env.VITE_APP_ASSETS_BASE_URL,
): PriceDisplay => {
  const display = sharedPriceDisplay(priceInfo, eventTokens);
  // A token missing from the catalog has no icon to tell the count from coins,
  // so the generic name stands in.
  const unknownToken = priceInfo?.source === "tokens" && !display.tokenName;
  return {
    iconUrl: priceIconUrl(display.icon, display.tokenIconKey, assetsBaseUrl),
    label: unknownToken ? tokensFallbackName() : display.label,
    showNumber: display.showNumber,
    displayPrice: display.displayPrice,
    launchPrice: display.launchPrice,
    tokenName: unknownToken ? tokensFallbackName() : display.tokenName,
  };
};

export interface ObjectiveDisplayInfo {
  iconUrl: string;
  /** Text beside the icon: season points or group name. `null` => icon alone. */
  text: string | null;
  /** Whether `text` is a free-form name that should be CSS-truncated with an ellipsis. */
  truncate: boolean;
}

/** The objective price cell: see the shared `getObjectiveDisplayInfo`. */
export const getObjectiveDisplayInfo = (
  objectiveDisplay: ObjectiveDisplay | undefined,
  assetsBaseUrl: string = import.meta.env.VITE_APP_ASSETS_BASE_URL,
): ObjectiveDisplayInfo => {
  const { icon, spRequired, groupName } =
    sharedObjectiveDisplayInfo(objectiveDisplay);
  return {
    iconUrl: `${assetsBaseUrl}/misc/${PRICE_ICON_FILES[icon]}`,
    text:
      spRequired != null ? convertPlayerPrice(spRequired) : (groupName ?? null),
    truncate: groupName != null,
  };
};
